export const LIVE_NOTIFICATION_TOAST_DURATION_MS = 5000;
export const LIVE_NOTIFICATION_TOAST_MAX_VISIBLE = 3;

// Per-user opt-out, stored in `ui_settings`. Missing key = alerts on.
export const LIVE_NOTIFICATION_TOASTS_DISABLED_KEY =
  'live_notification_toasts_disabled';

export const areLiveNotificationToastsEnabled = (uiSettings = {}) =>
  !uiSettings?.[LIVE_NOTIFICATION_TOASTS_DISABLED_KEY];

// `App.vue` calls `vueActionCable.init()` per account without disposing the
// previous connector, so the same `notification.created` can reach us more
// than once. We remember the last ids we announced to avoid double toasts.
const RECENT_IDS_LIMIT = 50;

export const getNotificationConversationId = notification =>
  notification?.primary_actor?.id ?? null;

export const getNotificationSenderName = notification =>
  notification?.primary_actor?.meta?.sender?.name?.trim() || '';

// `push_message_title` of a new message is "<sender>: <text>". The sender is
// already shown on its own line, so drop the prefix to avoid repeating it.
export const getNotificationMessagePreview = notification => {
  const text = notification?.push_message_title || '';
  const sender = getNotificationSenderName(notification);
  if (!sender || !text.startsWith(sender)) return text;
  // Names can end in an emoji, so the title may read "Ana 🙂 : hola".
  const match = text.slice(sender.length).match(/^\s*:\s*(.*)$/s);
  return match && match[1] ? match[1] : text;
};

export const isViewingConversation = (notification, currentConversationId) => {
  const conversationId = getNotificationConversationId(notification);
  if (conversationId === null || currentConversationId == null) return false;
  return String(conversationId) === String(currentConversationId);
};

export const shouldShowLiveNotificationToast = ({
  notification,
  currentAccountId,
  currentConversationId,
}) => {
  if (!notification) return false;
  if (Number(notification.account_id) !== Number(currentAccountId)) {
    return false;
  }
  // No conversation behind it (deleted) -> nothing to open, skip the toast.
  if (getNotificationConversationId(notification) === null) return false;
  return !isViewingConversation(notification, currentConversationId);
};

export const createRecentIdTracker = (limit = RECENT_IDS_LIMIT) => {
  const ids = [];
  return {
    // Returns true the first time an id is seen, false for repeats.
    markIfNew(id) {
      if (id == null) return true;
      if (ids.includes(id)) return false;
      ids.push(id);
      if (ids.length > limit) ids.shift();
      return true;
    },
  };
};

// Pushes a toast onto the stack and trims the oldest ones beyond `max`.
export const pushToast = (
  toasts,
  toast,
  max = LIVE_NOTIFICATION_TOAST_MAX_VISIBLE
) => {
  // One toast per conversation: a newer notification replaces the older one.
  const conversationId = getNotificationConversationId(toast.notification);
  const others = toasts.filter(
    existing =>
      conversationId === null ||
      getNotificationConversationId(existing.notification) !== conversationId
  );
  const next = [...others, toast];
  return next.length > max ? next.slice(next.length - max) : next;
};
